/**
 * Shared display formatters for the EditablePillRow and its pickers.
 *
 * Kept in a separate, component-free module so Vite's Fast Refresh stays
 * happy (a component file can't mix component and non-component exports).
 */

import { format, isValid, parseISO } from 'date-fns'
import type { AirportRef, Cabin, Passengers, TripType } from '@/lib/search/types'

export const CABIN_OPTIONS: ReadonlyArray<{ value: Cabin; label: string }> = [
  { value: 'economy', label: 'Economy' },
  { value: 'premium-economy', label: 'Premium Economy' },
  { value: 'business', label: 'Business' },
  { value: 'first', label: 'First' },
]

export function formatCabinLabel(cabin: Cabin): string {
  return CABIN_OPTIONS.find((o) => o.value === cabin)?.label ?? cabin
}

export function formatAirport(ref: AirportRef | undefined): string {
  if (!ref?.code) return '—'
  if (ref.label) {
    const cityMatch = ref.label.match(/^([^,(]+)/)
    const city = cityMatch ? cityMatch[1].trim() : ref.label
    return `${city} · ${ref.code}`
  }
  return ref.code
}

export function formatDateRange(
  departureDate: string | undefined,
  returnDate: string | undefined,
  tripType: TripType,
): string {
  const depart = formatDay(departureDate)
  if (tripType === 'one-way') return depart ?? '—'
  const ret = formatDay(returnDate)
  if (!depart && !ret) return '—'
  return `${depart ?? '—'} → ${ret ?? '—'}`
}

function formatDay(s: string | undefined): string | undefined {
  if (!s) return undefined
  const d = parseISO(s)
  if (!isValid(d)) return undefined
  return format(d, 'MMM d')
}

export function formatPassengers(p: Passengers): string {
  const parts: string[] = []
  parts.push(`${p.adults} ${p.adults === 1 ? 'adult' : 'adults'}`)
  if (p.children > 0) {
    parts.push(`${p.children} ${p.children === 1 ? 'child' : 'children'}`)
  }
  let summary = parts.join(' · ')
  if (p.children > 0 && p.childrenAges?.length) {
    const ages = p.childrenAges.slice(0, p.children)
    summary += ` (${ages.length === 1 ? 'age' : 'ages'} ${ages.join(', ')})`
  }
  return summary
}
